import * as React from 'react';
import {
  Plugins,
  TypeImplementation,
  Result,
  getMeta,
  Types,
  Type,
  Expressions,
  Component,
  TypeCheckerState,
  setValues,
  CheckerState,
  withSystem,
  getSystem,
} from '@packages/systems/wfdl';
import {
  iconName,
  namesEqual,
  qualify,
  typeName,
} from '@packages/systems/wfdl-Qualified';
import {
  ContextualExpressions,
  stripAnn,
  getAnn,
  ContextualExpression,
  stripAll,
  updateAnn,
} from '@packages/utilities/contextual-expression';
import {
  ELiteral,
  Expression,
  TEmptyRecord,
  TRecord,
  TTypeApplication,
  TTypeConstructor,
} from '@packages/systems/wfdl-ast';
import {Json, getString} from '@packages/utilities/json';
import {
  ExpressionBindMetaPayload,
  ExpressionBinding,
  updateExpressionAction,
  useExpressionEditorImplementationContext,
} from '@packages/systems/ExpressionEditor-utils';
import Hitarea from '@packages/systems/design-system/Hitarea';
import {
  Group,
  Select,
  Option,
} from '@packages/systems/design-system/WebflowSelect';
import {
  selectComponents,
  selectCurrentComponentType,
} from '@packages/utilities/platform-selectors';
import {pageComponentType, usePlugins} from '@packages/utilities/platform';
import {
  canCreateConditional,
  isConditional,
} from '@packages/systems/plugins-utils/Conditionals';
import {
  BASE_VARIANT_DEFAULTS,
  DEFAULT_STYLE_VARIANT_LABEL,
  getStyleVariantTypeArguments,
} from '@packages/systems/component-style-variants';
import {STYLE_VARIANT_RESET} from '@packages/systems/designer/actions';
import {DataTypeSummary} from '@packages/systems/ExpressionEditor-utils-ui';
import {
  useDesignerSelector,
  useDispatch,
} from '@packages/systems/designer/flux';

type VariantRenderProps = {
  state: string;
  onChange: (arg1: string) => void;
  dataType: Type;
  options: {id: string; displayName: string}[];
  isDisabled?: boolean;
};

/**
 * Update checker state to contain a local value which has the same type as the StyleVariant.
 * This allows binding to the StyleVariant to work correctly.
 */
function updateCheckerState(
  rootCheckerState: CheckerState,
  styleVariantId: string,
  componentName: string,
  pluginName: string
) {
  const checkerState = setValues({
    local: {
      // We need to keep the existing local values so that it is possible to bind also existing properties
      // to this Style Variant. TypeChecker will get rid of all the non compatible properties and leave only
      // properties which have same type as this StyleVariant. Meaning that it is only possible to bind properties
      // which have same styleVariantId, componentName and pluginName.
      ...rootCheckerState.env.context.values.local,
      // Only override the dataProperties field so that we include specifically this StyleVariant in the state
      dataProperties: {
        exported: 'visible',
        implicit: 'no',
        preferTypeBindUI: false,
        dataType: TTypeApplication({
          con: TTypeApplication({
            con: TTypeApplication({
              con: TTypeConstructor(
                qualify(Plugins.name('Basic'), 'StyleVariant')
              ),
              arg: TRecord({[styleVariantId]: TEmptyRecord}),
            }),
            arg: TRecord({[componentName]: TEmptyRecord}),
          }),
          arg: TRecord({[pluginName]: TEmptyRecord}),
        }),
      },
    },
    plugins: rootCheckerState.env.context.values.plugins,
  })(rootCheckerState);
  return checkerState;
}

const getVariantOptions = (
  pluginName: string,
  componentName: string,
  styleVariantId: string,
  componentMap: Component.ComponentMap
) => {
  const component = componentMap.get(qualify(pluginName, componentName));
  if (!component) {
    return null;
  }
  const variants = component.variants;
  if (!variants) {
    return null;
  }
  return variants[styleVariantId];
};

const VariantDropdownMenu = ({
  state,
  onChange,
  dataType,
  options,
  isDisabled,
}: VariantRenderProps) => {
  const {env} = useExpressionEditorImplementationContext();
  const meta = getMeta(dataType) || {};
  const elementId = Types.generateAutomationIds(dataType);
  return (
    <Hitarea>
      <Select
        id={elementId}
        data-automation-id={elementId}
        value={state}
        disabled={options.length === 0 || isDisabled}
        placeholder={meta.placeholder}
        onChange={(value, {changeType}) => {
          if (changeType === 'changeStart') {
            env.transaction.begin();
          } else if (changeType === 'changeEnd') {
            env.transaction.commit();
          } else if (changeType === 'changeCancel') {
            env.transaction.abort();
          } else {
            onChange(value);
          }
        }}
      >
        <Group>
          {options.map(({id, displayName}) => {
            return (
              <Option
                key={id}
                value={id}
                data-automation-id={`${elementId}-option-${id}`}
                aria-describedby={`${elementId}-hover-note-${id}`}
                label={<span>{displayName}</span>}
                singleLine
              />
            );
          })}
        </Group>
      </Select>
    </Hitarea>
  );
};

const StyleVariantRender = ({
  expr,
  dispatch,
}: {
  expr: ContextualExpression;
  dispatch: TypeImplementation.ExpressionEditorDispatch;
}) => {
  const [rootCheckerState, rootContextualExpr] =
    React.useContext(TypeCheckerState);
  const {env} = useExpressionEditorImplementationContext();
  const customDispatch = useDispatch();
  const componentMap = useDesignerSelector(selectComponents);
  const currentComponent = useDesignerSelector(selectCurrentComponentType);
  const plugins = usePlugins();
  const literal = ContextualExpressions.getLiteral(stripAnn(expr));
  const literalDispatch = (variantId: string) => {
    dispatch(
      updateExpressionAction(
        ELiteral({
          name: qualify(Plugins.name('Basic'), typeName('StyleVariant')),
          value: variantId,
        })
      )
    );
  };
  const onTopLevelBindChange = React.useCallback(
    (bind: null | Expression, meta: ExpressionBindMetaPayload) => {
      if (bind === null) {
        const selectExpr = stripAll(expr);
        const select = Expressions.getSelect(selectExpr);
        const propKey = select?.prop || '';
        dispatch({
          type: 'STYLE_VARIANT_DISCONNECTED',
          key: [],
          propKey,
        });
      } else {
        dispatch({
          type: 'EXPRESSION_BIND_CHANGED',
          binding: bind,
          meta,
          key: [],
        });
        // When creating a conditional from a bound style variant,
        // also dispatch STYLE_VARIANT_DISCONNECTED to potentially clean up the property
        if (
          isConditional(bind) &&
          ContextualExpressions.isBinding(stripAnn(expr))
        ) {
          const selectExpr = stripAll(expr);
          const select = Expressions.getSelect(selectExpr);
          const propKey = select?.prop || '';
          dispatch({
            type: 'STYLE_VARIANT_DISCONNECTED',
            key: [],
            propKey,
          });
        }
      }
    },
    [dispatch, expr]
  );
  const exprAnn = getAnn(expr);
  const type = exprAnn.type;
  const {variantFieldId, componentName, pluginName} = type
    ? getStyleVariantTypeArguments(type)
    : {
        variantFieldId: undefined,
        componentName: undefined,
        pluginName: undefined,
      };
  const variantOptions =
    variantFieldId && componentName && pluginName
      ? getVariantOptions(
          pluginName,
          componentName,
          variantFieldId,
          componentMap
        )
      : null;
  const value = literal ? getString(literal.value) : null;
  const path = exprAnn.env.expressionPath.slice(-1);
  const rootLiteral = rootContextualExpr
    ? ContextualExpressions.getLiteral(stripAnn(rootContextualExpr))
    : null;

  const onDeletedStyleVariantOption = React.useCallback(() => {
    const isDefaultValue = rootLiteral !== null;
    customDispatch({
      type: STYLE_VARIANT_RESET,
      payload: {
        key: path,
        isDefaultValue,
        deletedVariantId: value,
      },
    });
  }, [customDispatch, path, rootLiteral, value]);

  React.useEffect(() => {
    if (ContextualExpressions.isBinding(stripAnn(expr))) {
      return;
    }
    // If a deleted variant is selected we should reset the expression to the default value.
    const isDeletedVariantSelected =
      variantOptions && !variantOptions.some((option) => option.id === value);
    if (isDeletedVariantSelected) {
      onDeletedStyleVariantOption();
    }
  }, [onDeletedStyleVariantOption, expr, value, variantOptions]);
  if (ContextualExpressions.isBinding(stripAnn(expr))) {
    return (
      <ExpressionBinding
        expression={expr}
        onChange={onTopLevelBindChange}
        bindUIVariant="dot"
        automationLabel="Variant"
      />
    );
  }
  if (
    !value ||
    !type ||
    !pluginName ||
    !componentName ||
    !variantFieldId ||
    !rootContextualExpr
  ) {
    return null;
  }
  if (!variantOptions) {
    return (
      <VariantDropdownMenu
        state={BASE_VARIANT_DEFAULTS.id}
        onChange={literalDispatch}
        dataType={type}
        options={[BASE_VARIANT_DEFAULTS]}
        isDisabled
      />
    );
  }

  const effectiveValue = variantOptions.some((opt) => opt.id === value)
    ? value
    : BASE_VARIANT_DEFAULTS.id;

  const isLiteralOrOnPageComponentContext =
    rootLiteral || namesEqual(currentComponent, pageComponentType);

  // If the StyleVariant expression is literal or on page component context, skip updating the
  // checker state with dataProperties so that the variant prop creation is not available as you cannot
  // create a Variant prop from page or prop default value context.
  const checkerState = isLiteralOrOnPageComponentContext
    ? rootCheckerState
    : updateCheckerState(
        rootCheckerState,
        variantFieldId,
        componentName,
        pluginName
      );
  const canCreateVariantConditional = canCreateConditional({
    type,
    rootExpr: rootContextualExpr,
    expressionPath: getAnn(expr).env.expressionPath,
    plugins,
    checkerState,
  });

  // If we are viewing the default value of the expression or we are on the page component
  // show only the VariantDropdownMenu instead of any bindings.
  // We are using rootLiteral here to check if we are here in the context of the default value.
  // In other contexts rootLiteral will be null as the root expression is the element.
  // Exception: if conditionals can be created from available operands, fall through to
  // the ExpressionBinding wrapper so the binding dot is rendered.
  if (isLiteralOrOnPageComponentContext && !canCreateVariantConditional) {
    return (
      <VariantDropdownMenu
        state={effectiveValue}
        onChange={literalDispatch}
        dataType={type}
        options={variantOptions}
        isDisabled={env.uiFlags?.disabled?.expression?.(expr)}
      />
    );
  }
  // Update the expression to have the correct environment
  // Update the system to have isBindable set to true as StyleVariant is bindable always here
  const updatedExpr = updateAnn((ann) => {
    const updatedEnv = checkerState.env;
    return {
      ...ann,
      type: ann.type
        ? withSystem({...getSystem(ann.type), isBindable: true}, ann.type)
        : ann.type,
      env: {...updatedEnv, expressionPath: ann.env.expressionPath},
    };
  }, expr);
  return (
    <TypeCheckerState.Provider value={[checkerState, rootContextualExpr]}>
      <ExpressionBinding
        expression={updatedExpr}
        onChange={onTopLevelBindChange}
        bindUIVariant="custom"
      >
        <VariantDropdownMenu
          state={effectiveValue}
          onChange={literalDispatch}
          dataType={type}
          options={variantOptions}
        />
      </ExpressionBinding>
    </TypeCheckerState.Provider>
  );
};

const SVRenderSummary = ({expr}: {expr: ContextualExpression}) => {
  const componentMap = useDesignerSelector(selectComponents);

  // If expression is a binding render back to DataTypeSummary
  if (ContextualExpressions.isBinding(stripAnn(expr))) {
    return <DataTypeSummary context={expr} />;
  }

  const literal = ContextualExpressions.getLiteral(stripAnn(expr));
  const value = literal ? getString(literal.value) : null;

  const exprAnn = getAnn(expr);
  const type = exprAnn.type;
  const {variantFieldId, componentName, pluginName} = type
    ? getStyleVariantTypeArguments(type)
    : {
        variantFieldId: undefined,
        componentName: undefined,
        pluginName: undefined,
      };
  const variantOptions =
    variantFieldId && componentName && pluginName
      ? getVariantOptions(
          pluginName,
          componentName,
          variantFieldId,
          componentMap
        )
      : null;

  // Find displayName for our value
  const matchedOption = variantOptions?.find((option) => option.id === value);

  if (matchedOption) {
    return <span>{matchedOption.displayName}</span>;
  }

  if (variantOptions && value) {
    return <span>{BASE_VARIANT_DEFAULTS.displayName}</span>;
  }

  return <span>Variant</span>;
};

const StyleVariant = TypeImplementation.Custom({
  params: ['styleVariantId', 'componentName', 'pluginName'],
  parseValue: (json: Json) => {
    return typeof json === 'string'
      ? Result.Ok(json)
      : Result.Error('Expected a string for StyleVariant');
  },
  render: StyleVariantRender,
  icon: TypeImplementation.IconByName(
    qualify(Plugins.name('Basic'), iconName('StyleVariant'))
  ),
  system: {
    bindUIVariant: 'custom',
    displayName: DEFAULT_STYLE_VARIANT_LABEL,
    label: DEFAULT_STYLE_VARIANT_LABEL,
  },
  renderSummary: SVRenderSummary,
});

export default StyleVariant;
